import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline'
import { api } from '@lipaflow/shared'
interface Readiness {
  status: 'ready' | 'not_ready'
  database: string
  redis: string
  outbound_enabled: boolean
  ai_enabled: boolean
}

/**
 * Live readiness from the API. This panel used to be hard-coded — "WhatsApp
 * Connected", "M-Pesa Active", "99.9% uptime" — under a small "Demo" badge,
 * while WhatsApp was not wired at all. Show only what is actually checked.
 */
export function PlatformHealth() {
  const root = String(api.defaults.baseURL ?? '').replace(/\/api\/v1\/?$/, '')
  const { data, isLoading, isError } = useQuery({
    queryKey: ['platform', 'readiness'],
    queryFn: async () => {
      // /health/ready answers 503 with the same body when something is down.
      const res = await api.get<Readiness>('/health/ready', { baseURL: root, validateStatus: (s) => s === 200 || s === 503 })
      return res.data
    },
    refetchInterval: 60_000,
  })

  const rows: { label: string; ok: boolean; value: string }[] = data
    ? [
        { label: 'API', ok: data.status === 'ready', value: data.status === 'ready' ? 'Ready' : 'Not ready' },
        { label: 'Database', ok: data.database === 'connected', value: data.database === 'connected' ? 'Connected' : 'Not reachable' },
        { label: 'Cache and background jobs', ok: data.redis === 'connected', value: data.redis === 'connected' ? 'Connected' : 'Not reachable' },
        { label: 'Messages to payers', ok: data.outbound_enabled, value: data.outbound_enabled ? 'On' : 'Paused platform-wide' },
        { label: 'Amani replies', ok: data.ai_enabled, value: data.ai_enabled ? 'On' : 'Off platform-wide' },
      ]
    : []

  return (
    <div className="bg-surface rounded-card border border-hairline p-6">
      <h3 className="font-outfit text-h4 text-ink mb-1">Platform health</h3>
      <p className="font-inter text-caption text-ink-muted mb-4">Checked live every minute.</p>
      {isLoading ? (
        <p role="status" className="text-body-sm text-ink-muted">Checking…</p>
      ) : isError || !data ? (
        <p className="text-body-sm text-warning">Couldn't reach the API to check its health.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.label} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-body-sm text-ink">
                {r.ok ? <CheckCircleIcon className="h-5 w-5 text-success" /> : <ClockIcon className="h-5 w-5 text-warning" />}
                {r.label}
              </span>
              <span className={clsx('text-body-sm font-medium', r.ok ? 'text-success' : 'text-warning')}>{r.value}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-caption text-ink-faint">
        WhatsApp and M-PESA connections are set per customer and aren't checked here.
      </p>
    </div>
  )
}

